import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { DONATE_URL, SUBSTACK_URL, BADGE_URL, STORE_URL } from "@/lib/api";
import { SocialLinks } from "@/components/SocialLinks";

const links = [
  { to: "/articles", label: "Articles" },
  { to: "/photojournalism", label: "Photojournalism" },
  { to: "/events", label: "Events" },
  { to: STORE_URL, label: "Shop", external: true },
  { to: "/partner", label: "Partner" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 bg-white/95 backdrop-blur-md border-b ${
        scrolled ? "border-ink/10 shadow-sm" : "border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-[92px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={BADGE_URL} alt="The Canary Report" className="h-[72px] w-[72px] rounded-full object-cover transition-transform duration-300 group-hover:scale-105" />
          <span className="hidden sm:block font-display font-extrabold text-xl lg:text-2xl leading-none tracking-tight">The Canary Report</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="label link-underline pb-1 text-ink/60 hover:text-ink"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `label link-underline pb-1 ${isActive ? "text-ink" : "text-ink/60 hover:text-ink"}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <a
            href={DONATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-donate-btn"
            className="label bg-ink text-white px-6 py-3 rounded-full hover:bg-canary hover:text-ink transition-colors duration-300"
          >
            Donate
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="xl:hidden h-10 w-10 flex items-center justify-center"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden overflow-hidden bg-white border-b border-ink/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {links.map((l) =>
                l.external ? (
                  <a
                    key={l.to}
                    href={l.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="font-display text-2xl font-bold"
                  >
                    {l.label}
                  </a>
                ) : (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="font-display text-2xl font-bold"
                  >
                    {l.label}
                  </NavLink>
                )
              )}
              <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-subscribe" className="font-display text-2xl font-bold">Subscribe</a>
              <a
                href={DONATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-donate-btn"
                className="label bg-ink text-white px-6 py-4 rounded-full text-center"
              >
                Donate
              </a>
              <div data-testid="mobile-nav-socials" className="pt-5 border-t border-ink/10">
                <p className="label text-ink/40 mb-3">Follow us</p>
                <SocialLinks group="brand" variant="light" testidPrefix="mobile-social" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
